import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteAuthenticated, putAuthenticated } from './api';
import {
  UploadResponseItem,
  UploadResponse,
  CloudinaryUpdateRequest,
} from '../models/cloudinary.models';
import { useGetSignedUserOrUserFromState } from '../hooks/auth';

// Note: The queries in this file make calls to cloudinary to retrieve/edit images.
// We are also updating the cache directly and persisting to local storage on success of these calls.
// This is to work around a significant lag fron cloudinary when updating or uploading images.

const stage = import.meta.env.VITE_STAGE === 'dev' ? 'dev' : 'prod';
const cloudinaryPreset = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET as string;

export const cloudinaryBaseUrl = `https://res.cloudinary.com/mcm-${stage}/`;

export const useUploadImage = () => {
  const url = `https://api.cloudinary.com/v1_1/mcm-${stage}/image/upload`;
  const user = useGetSignedUserOrUserFromState();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['images', user?.userId],
    mutationFn: async (image: FormData) => {

      let uploadPresetName;
      if (stage !== 'dev') {
        uploadPresetName = cloudinaryPreset;
        image.append('upload_preset', uploadPresetName);
      }

      const res = await fetch(url, {
        method: 'POST',
        body: image,
      });
      return (await res.json()) as never as UploadResponseItem;
    },
    onSuccess: (data) => {
      // Update cache with data returned from cloudinary upload response.
      const res = {
        public_id: data.public_id,
        version: data.version,
        signature: data.signature,
        width: data.width,
        height: data.height,
        format: data.format,
        resource_type: data.resource_type,
        created_at: data.created_at,
        bytes: data.bytes,
        type: data.type,
        url: data.url,
        secure_url: data.secure_url,
        metadata: [
          {
            external_id: 'priority_rank',
            label: 'priority_rank',
            value: data.metadata['priority_rank' as keyof typeof data.metadata],
          },
          {
            external_id: 'image_notes',
            label: 'image_notes',
            value: '',
          },
        ],
      };
      queryClient.setQueryData(
        ['images', user?.userId],
        (old: UploadResponseItem[]) => {
          return old ? [res, ...old] : [res];
        }
      );
    },
  });
};

export const useDeleteImage = () => {
  const url = `portal/style/image`;
  const user = useGetSignedUserOrUserFromState();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['images', user?.userId],
    mutationFn: async (public_id: string) => {
      return deleteAuthenticated<string>(
        url,
        JSON.stringify({ public_id: public_id })
      );
    },
    onSuccess: async (_data, public_id: string) => {
      await queryClient.setQueryData(
        ['images', user?.userId],
        (old: UploadResponseItem[]) => {
          return old.filter((item) => item.public_id !== public_id);
        }
      );
    },
  });
};

export const useUpdateImage = () => {
  const url = `portal/style/image`;
  const user = useGetSignedUserOrUserFromState();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['images', user?.userId],
    mutationFn: async (request: CloudinaryUpdateRequest) => {
      return putAuthenticated<string>(url, JSON.stringify(request));
    },
    onMutate: (request: CloudinaryUpdateRequest) => {
      queryClient.setQueryData(
        ['images', user?.userId],
        (old: UploadResponseItem[]) => {
          return old.map((image) => {
            if (image.public_id === request.public_id) {
              const metadata = image.metadata.map((item) => {
                if (item.label === 'image_notes') {
                  return { ...item, value: request.image_notes };
                }
                return item;
              });
              return { ...image, metadata };
            }
            return image;
          });
        }
      );
    },
  });
};

export const useUpdateImageRank = () => {
  const url = `portal/style/image`;
  const user = useGetSignedUserOrUserFromState();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['images', user?.userId],
    mutationFn: async (request: {
      image: UploadResponseItem;
      rank: number;
    }): Promise<{ image: UploadResponseItem; rank: number }> => {
      await putAuthenticated<string>(
        url,
        JSON.stringify({
          public_id: request.image.public_id,
          priority_rank: request.rank,
        })
      );
      return request;
    },
    onMutate: (request: { image: UploadResponseItem; rank: number }) => {
      queryClient.setQueryData(
        ['images', user?.userId],
        (old: UploadResponseItem[]) => {
          return old.map((image) => {
            if (image.public_id === request.image.public_id) {
              const metadata = image.metadata.map((item) => {
                if (item.label === 'priority_rank') {
                  return { ...item, value: request.rank };
                }
                return item;
              });
              return { ...image, metadata };
            }
            return image;
          });
        }
      );
    },
  });
};

export const useGetImages = (sub: string) => {
  const url = `https://res.cloudinary.com/mcm-${stage}/image/list/${sub}-style-board.json`;
  const { isPending, error, data } = useQuery({
    queryKey: ['images', sub],
    enabled: !!sub,
    queryFn: async () => {
      const res = (await fetch(url, {})).json() as Promise<UploadResponse>;
      return (await res).resources;
    },
    staleTime: 60 * 1000,
    meta: {
      persist: true,
    },
  });

  return {
    loading: isPending,
    error,
    data: (data as UploadResponseItem[]) || [],
  };
};
